/* eslint-disable jsx-a11y/anchor-is-valid */

import * as Yup from 'yup'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { useAuth } from 'app/context/auth-context'
import { useFormik, FormikHelpers } from 'formik'
import { useState, useCallback, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap-v5'
import Confirmation from './Confirmation'

// Type definitions
interface LoginFormValues {
  email: string
  password: string
}

interface PasswordChangeFormValues {
  newPassword: string
  confirmPassword: string
}

interface CognitoUser {
  challengeName?: string
  session?: string
  challengeParam?: Record<string, any>
  userAttributes?: Record<string, any>
  username?: string
}

interface AuthResponse {
  challengeName?: string
  session?: string
  user?: CognitoUser
}

interface AuthError extends Error {
  name: string
  code?: string
  message: string
}

interface ErrorInfo {
  type: ErrorType
  message: string
}

// Error types enum
enum ErrorType {
  NETWORK = 'NETWORK_ERROR',
  AUTHENTICATION = 'AUTH_ERROR',
  VALIDATION = 'VALIDATION_ERROR',
  SERVER = 'SERVER_ERROR',
  UNKNOWN = 'UNKNOWN_ERROR'
}

// Auth context interface (extend as needed based on your actual auth context)
interface AuthContextType {
  login: (email: string, password: string) => Promise<AuthResponse>
  completeNewPassword: (user: CognitoUser, newPassword: string) => Promise<void>
  // Add other auth methods as needed
}

// Enhanced validation schemas
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .min(3, 'Email must be at least 3 characters')
    .max(254, 'Email must not exceed 254 characters') // RFC 5321 limit
    .required('Email is required')
    .trim(),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must not exceed 128 characters')
    .required('Password is required'),
})

const changePasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must not exceed 128 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    )
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your password'),
})

// Initial form values
const initialValues: LoginFormValues = {
  email: '',
  password: '',
}

const initialPasswordValues: PasswordChangeFormValues = {
  newPassword: '',
  confirmPassword: '',
}

// Utility function to check internet connectivity
const checkConnectivity = async (): Promise<boolean> => {
  if (!navigator.onLine) {
    return false
  }

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

    await fetch('https://www.google.com/favicon.ico', {
      mode: 'no-cors',
      signal: controller.signal
    })

    clearTimeout(timeoutId)
    return true
  } catch {
    return false
  }
}

// Utility function to determine error type and message
const getErrorInfo = (error: AuthError | Error | unknown): ErrorInfo => {
  const authError = error as AuthError

  if (!navigator.onLine) {
    return {
      type: ErrorType.NETWORK,
      message: 'No internet connection. Please check your network and try again.'
    }
  }

  if (authError?.name === 'NetworkError' || authError?.code === 'NetworkError') {
    return {
      type: ErrorType.NETWORK,
      message: 'Network error. Please check your connection and try again.'
    }
  }

  if (authError?.name === 'NotAuthorizedException' || authError?.message?.includes('auth')) {
    return {
      type: ErrorType.AUTHENTICATION,
      message: 'Invalid email or password. Please try again.'
    }
  }

  if (authError?.name === 'UserNotFoundException') {
    return {
      type: ErrorType.AUTHENTICATION,
      message: 'Account not found. Please check your email or create a new account.'
    }
  }

  if (authError?.name === 'TooManyRequestsException') {
    return {
      type: ErrorType.SERVER,
      message: 'Too many login attempts. Please wait a few minutes before trying again.'
    }
  }

  if (authError?.name === 'UserNotConfirmedException') {
    return {
      type: ErrorType.AUTHENTICATION,
      message: 'Please verify your email address before signing in.'
    }
  }

  if (authError?.name === 'PasswordResetRequiredException') {
    return {
      type: ErrorType.AUTHENTICATION,
      message: 'Password reset is required. Please use the forgot password link.'
    }
  }

  return {
    type: ErrorType.UNKNOWN,
    message: 'An unexpected error occurred. Please try again later.'
  }
}

export function Login(): JSX.Element {
  // State management with proper types
  const [loading, setLoading] = useState<boolean>(false)
  const [showChangePassword, setShowChangePassword] = useState<boolean>(false)
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine)
  const [loginAttempts, setLoginAttempts] = useState<number>(0)
  const [isBlocked, setIsBlocked] = useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [confirmationEmail, setConfirmationEmail] = useState<string>('')
  const [confirmationPassword, setConfirmationPassword] = useState<string>('')

  // Hooks and refs
  const authContext = useAuth() as AuthContextType
  const search = useLocation().search
  const email = new URLSearchParams(search).get("email") || ''
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Monitor network connectivity
  useEffect(() => {
    const handleOnline = (): void => setIsOnline(true)
    const handleOffline = (): void => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Rate limiting - block after 5 failed attempts for 15 minutes
  useEffect(() => {
    if (loginAttempts >= 5) {
      setIsBlocked(true)
      timeoutRef.current = setTimeout(() => {
        setIsBlocked(false)
        setLoginAttempts(0)
      }, 15 * 60 * 1000) // 15 minutes
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [loginAttempts])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const togglePasswordVisibility = useCallback((): void => {
    setShowPassword(prev => !prev)
  }, [])

  // Login form configuration
  const formik = useFormik<LoginFormValues>({
    initialValues: { ...initialValues, email },
    validationSchema: loginSchema,
    onSubmit: async (
      values: LoginFormValues,
      { setStatus, setSubmitting }: FormikHelpers<LoginFormValues>
    ): Promise<void> => {
      if (isBlocked) {
        setStatus('Too many failed attempts. Please wait 15 minutes before trying again.')
        setSubmitting(false)
        return
      }

      setLoading(true)
      setStatus('')

      try {
        // Check connectivity before making API call
        const hasConnection = await checkConnectivity()
        if (!hasConnection) {
          throw new Error('NETWORK_ERROR')
        }

        const response = await authContext.login(values.email, values.password)

        if (response?.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setCognitoUser(response as CognitoUser)
          setShowChangePassword(true)
        }

        // Reset login attempts on successful login
        setLoginAttempts(0)
      } catch (error: unknown) {
        console.error('Login error:', error)
        const authError = error as AuthError
        // If user is not confirmed, open confirmation modal so they can confirm and sign in
        if (authError?.name === 'UserNotConfirmedException' || authError?.code === 'UserNotConfirmedException') {
          setConfirmationEmail(values.email)
          setConfirmationPassword(values.password)
          setShowConfirmation(true)
          setStatus('Account not confirmed. Please enter the confirmation code sent to your email.')
          setLoading(false)
          setSubmitting(false)
          return
        }
        const errorInfo = getErrorInfo(error)

        if (errorInfo.type === ErrorType.AUTHENTICATION) {
          setLoginAttempts(prev => prev + 1)
        }

        setStatus(errorInfo.message)
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  // Password change form configuration
  const passwordFormik = useFormik<PasswordChangeFormValues>({
    initialValues: initialPasswordValues,
    validationSchema: changePasswordSchema,
    onSubmit: async (
      values: PasswordChangeFormValues,
      { setStatus, setSubmitting }: FormikHelpers<PasswordChangeFormValues>
    ): Promise<void> => {
      if (!cognitoUser) {
        setStatus('Session expired. Please try logging in again.')
        setSubmitting(false)
        return
      }

      setLoading(true)
      setStatus('')

      try {
        const hasConnection = await checkConnectivity()
        if (!hasConnection) {
          throw new Error('NETWORK_ERROR')
        }

        await authContext.completeNewPassword(cognitoUser, values.newPassword)
        await authContext.login(formik.values.email, values.newPassword)

        setShowChangePassword(false)
        // Reset forms
        formik.resetForm()
        passwordFormik.resetForm()
        setCognitoUser(null)
      } catch (error: unknown) {
        console.error('Password change error:', error)
        const errorInfo = getErrorInfo(error)
        setStatus(errorInfo.message)
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  const handleCloseModal = useCallback((): void => {
    if (!loading) {
      setShowChangePassword(false)
      passwordFormik.resetForm()
      setCognitoUser(null)
    }
  }, [loading, passwordFormik])

  // Event handlers with proper typing
  const handleEmailInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    formik.handleChange(e)
  }, [formik])

  const handlePasswordInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    formik.handleChange(e)
  }, [formik])

  const handleNewPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    passwordFormik.handleChange(e)
  }, [passwordFormik])

  const handleConfirmPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    passwordFormik.handleChange(e)
  }, [passwordFormik])

  return (
    <>
      <div className='w-lg-600px bg-white rounded shadow-sm p-8 p-lg-10 mx-auto'>
        {/* Network status indicator */}
        {!isOnline && (
          <div className='alert alert-warning mb-5' role='alert'>
            <i className='bi bi-wifi-off me-2' aria-hidden='true'></i>
            You are currently offline. Please check your internet connection.
          </div>
        )}

        {/* Rate limiting warning */}
        {isBlocked && (
          <div className='alert alert-danger mb-5' role='alert'>
            <i className='bi bi-shield-exclamation me-2' aria-hidden='true'></i>
            Account temporarily locked due to multiple failed login attempts. Please try again in 15 minutes.
          </div>
        )}

        <form
          className='form w-100'
          onSubmit={formik.handleSubmit}
          noValidate
          id='kt_login_signin_form'
        >
          {/* begin::Heading */}
          <div className='text-center mb-5'>
            <h1 className='text-dark mb-3 fs-2'>Recruiter / Employer Admin - Sign In</h1>
          </div>
          {/* end::Heading */}

          {formik.status && (
            <div className='mb-lg-15 alert alert-danger' role='alert'>
              <div className='alert-text font-weight-bold'>
                <i className='bi bi-exclamation-triangle me-2' aria-hidden='true'></i>
                {formik.status}
              </div>
            </div>
          )}

          {/* Login attempts warning */}
          {loginAttempts > 0 && loginAttempts < 5 && (
            <div className='mb-lg-15 alert alert-warning' role='alert'>
              <div className='alert-text font-weight-bold'>
                <i className='bi bi-exclamation-circle me-2' aria-hidden='true'></i>
                {5 - loginAttempts} attempts remaining before temporary lockout.
              </div>
            </div>
          )}

          {/* begin::Form group - Email */}
          <div className='fv-row mb-7'>
            <label htmlFor='email' className='form-label fs-7 fw-bolder text-dark'>
              Email
            </label>
            <input
              id='email'
              placeholder='Enter your email address'
              {...formik.getFieldProps('email')}
              onChange={handleEmailInputChange}
              className={clsx(
                'form-control form-control-solid',
                { 'is-invalid': formik.touched.email && formik.errors.email },
                { 'is-valid': formik.touched.email && !formik.errors.email }
              )}
              type='email'
              name='email'
              autoComplete='email'
              disabled={loading || isBlocked}
              maxLength={254}
              aria-describedby={formik.touched.email && formik.errors.email ? 'email-error' : undefined}
            />
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger' id='email-error'>
                  <i className='bi bi-exclamation-circle me-1' aria-hidden='true'></i>
                  {formik.errors.email}
                </span>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group - Password */}
          <div className='fv-row mb-7'>
            <div className='d-flex justify-content-between'>
              <div className='d-flex flex-stack mb-1'>
                <label htmlFor='password' className='form-label fw-bolder text-dark fs-7 mb-0'>
                  Password
                </label>
              </div>
            </div>
            <div className="input-group">
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                placeholder='Enter your password'
                autoComplete='current-password'
                {...formik.getFieldProps('password')}
                onChange={handlePasswordInputChange}
                className={clsx(
                  'form-control form-control-solid',
                  { 'is-invalid': formik.touched.password && formik.errors.password },
                  { 'is-valid': formik.touched.password && !formik.errors.password }
                )}
                disabled={loading || isBlocked}
                maxLength={128}
                aria-describedby={formik.touched.password && formik.errors.password ? 'password-error' : undefined}
              />
              <button
                title={showPassword ? 'Hide password' : 'Show password'}
                type='button'
                className='btn btn-outline-secondary'
                onClick={togglePasswordVisibility}
                disabled={loading || isBlocked}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} aria-hidden='true'></i>
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert' className='text-danger' id='password-error'>
                    <i className='bi bi-exclamation-circle me-1' aria-hidden='true'></i>
                    {formik.errors.password}
                  </span>
                </div>
              </div>
            )}

            <div className='d-flex justify-content-end'>
              <Link
                to={'/auth/forgot-password?email=' + encodeURIComponent(formik.values.email)}
                className='link-primary fs-7 mt-1'
              >
                Forgot Password?
              </Link>
            </div>
          </div>
          {/* end::Form group */}

          <div className='text-center mb-4'>
            <div className='text-gray-400 fw-bold fs-6'>
              <div className='text-center text-gray-500 fs-7 mt-5 px-5 lh-lg'>
                If you're the designated Employer Admin and don't have an account, click the link below
              </div>
            <Link to='/auth/registration'>
              <span className='fw-bold text-primary'>Create an Employer-Administrator account</span>
            </Link>
          </div>
      </div>

      {/* begin::Action */}
      <div className='text-center'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary w-100 mb-4'
          disabled={formik.isSubmitting || !formik.isValid || loading || isBlocked || !isOnline}
          aria-describedby='submit-button-status'
        >
          {!loading && <span className='indicator-label'>Continue</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...
              <span
                className='spinner-border spinner-border-sm align-middle ms-2'
                role='status'
                aria-hidden='true'
              ></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      <div className='text-center text-gray-500 fs-7 mt-5 px-5 lh-lg'>
        <i className='bi bi-info-circle me-1'></i>
        If you are a job seeker / applicant, please use the dedicated job fair link advertised or shared with you to sign in.
      </div>
    </form >
      </div >

    {/* Confirmation Modal (for unconfirmed users) */ }
    < Confirmation
  asModal
  show = { showConfirmation }
  autoResend = { true}
  email = { confirmationEmail }
  password = { confirmationPassword }
  onConfirmed = { async() => {
    setShowConfirmation(false)
    try {
      await authContext.login(confirmationEmail, confirmationPassword)
      // After successful sign in you may want to redirect or refresh state
      setConfirmationEmail('')
      setConfirmationPassword('')
      formik.resetForm()
    } catch (err) {
      console.warn('Sign in after confirmation failed', err)
    }
  }
}
onCancel = {() => setShowConfirmation(false)}
      />

{/* Password Change Modal */ }
<Modal
  show={showChangePassword}
  backdrop="static"
  keyboard={false}
  onHide={handleCloseModal}
  aria-labelledby='password-change-modal-title'
>
  <Modal.Header>
    <Modal.Title id='password-change-modal-title'>
      <i className='bi bi-shield-lock me-2' aria-hidden='true'></i>
      Password Change Required
    </Modal.Title>
  </Modal.Header>
  <form onSubmit={passwordFormik.handleSubmit} noValidate>
    <Modal.Body>
      <div className='alert alert-info mb-4' role='alert'>
        <i className='bi bi-info-circle me-2' aria-hidden='true'></i>
        For security reasons, you must set a new password before continuing.
      </div>

      {passwordFormik.status && (
        <div className='alert alert-danger mb-4' role='alert'>
          <i className='bi bi-exclamation-triangle me-2' aria-hidden='true'></i>
          {passwordFormik.status}
        </div>
      )}

      <div className='fv-row mb-4'>
        <label htmlFor='newPassword' className='form-label fs-6 fw-bolder text-dark required'>
          New Password
        </label>
        <input
          id='newPassword'
          type='password'
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': passwordFormik.touched.newPassword && passwordFormik.errors.newPassword },
            { 'is-valid': passwordFormik.touched.newPassword && !passwordFormik.errors.newPassword }
          )}
          placeholder='Enter new password'
          {...passwordFormik.getFieldProps('newPassword')}
          onChange={handleNewPasswordChange}
          disabled={loading}
          maxLength={128}
          autoComplete='new-password'
          aria-describedby={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword ? 'new-password-error' : 'password-requirements'}
        />
        {passwordFormik.touched.newPassword && passwordFormik.errors.newPassword && (
          <div className='fv-plugins-message-container'>
            <span role='alert' className='text-danger' id='new-password-error'>
              <i className='bi bi-exclamation-circle me-1' aria-hidden='true'></i>
              {passwordFormik.errors.newPassword}
            </span>
          </div>
        )}
      </div>

      <div className='fv-row mb-4'>
        <label htmlFor='confirmPassword' className='form-label fs-6 fw-bolder text-dark required'>
          Confirm Password
        </label>
        <input
          id='confirmPassword'
          type='password'
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword },
            { 'is-valid': passwordFormik.touched.confirmPassword && !passwordFormik.errors.confirmPassword }
          )}
          placeholder='Confirm new password'
          {...passwordFormik.getFieldProps('confirmPassword')}
          onChange={handleConfirmPasswordChange}
          disabled={loading}
          maxLength={128}
          autoComplete='new-password'
          aria-describedby={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword ? 'confirm-password-error' : undefined}
        />
        {passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword && (
          <div className='fv-plugins-message-container'>
            <span role='alert' className='text-danger' id='confirm-password-error'>
              <i className='bi bi-exclamation-circle me-1' aria-hidden='true'></i>
              {passwordFormik.errors.confirmPassword}
            </span>
          </div>
        )}
      </div>

      <div className='alert alert-light' id='password-requirements'>
        <strong>Password Requirements:</strong>
        <ul className='mb-0 mt-2'>
          <li>At least 8 characters long</li>
          <li>Contains uppercase and lowercase letters</li>
          <li>Contains at least one number</li>
          <li>Contains at least one special character (@$!%*?&)</li>
        </ul>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <button
        type='submit'
        className='btn btn-lg btn-primary w-100'
        disabled={loading || !passwordFormik.isValid || passwordFormik.isSubmitting}
      >
        {!loading && <span className='indicator-label'>Change Password</span>}
        {loading && (
          <span className='indicator-progress' style={{ display: 'block' }}>
            Please wait...
            <span
              className='spinner-border spinner-border-sm align-middle ms-2'
              role='status'
              aria-hidden='true'
            ></span>
          </span>
        )}
      </button>
    </Modal.Footer>
  </form>
</Modal>
    </>
  )
}
